import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext.js';
import { trackPageView } from './config/analyticsConfig.js';
import Dashboard from './components/Dashboard.js';
import Login from './components/Login.js';
import ForgotPassword from './components/ForgotPassword.js';
import EmailVerification from './components/EmailVerification.js';
import Profile from './components/Profile.js';
import CheckoutSuccess from './components/CheckoutSuccess.js';
import Reviews from './components/Reviews.js';
import Schedules from './components/Schedules.js';
import Settings from './components/Settings.js';
import ScheduleSettings from './components/ScheduleSettings.js';
import CreateEntity from './components/CreateEntity.js';
import FinalizeAppointment from './components/FinalizeAppointment.js';
import Discover from './pages/Discover.js';
import './App.css';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '1.2rem',
          color: '#666'
        }}>
          Carregando...
        </div>
      </div>
    );
  }

  if (user && user.emailVerified === false) {
    return <Navigate to="/verify-email" replace />;
  }

  return user ? children : <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '1.2rem',
          color: '#666'
        }}>
          Carregando...
        </div>
      </div>
    );
  }

  return !user ? children : <Navigate to="/dashboard" replace />;
}

// Hook para rastrear mudanças de página
function usePageTracking() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location]);
}

function AppContent() {
  const { user, logout } = useAuth();
  usePageTracking();

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        <Route 
          path="/forgot-password" 
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          } 
        />
        <Route path="/verify-email" element={<EmailVerification />} />
        <Route 
          path="/dashboard" 
          element={
            <PrivateRoute>
              <Dashboard user={user} onLogout={logout} />
            </PrivateRoute>
          } 
        />
        <Route 
          path="/reviews" 
          element={
            <PrivateRoute>
              <Dashboard user={user} onLogout={logout} />
            </PrivateRoute>
          } 
        />
        <Route
          path="/patients"
          element={
            <PrivateRoute>
              <Dashboard user={user} onLogout={logout} />
            </PrivateRoute>
          }
        />
        <Route 
          path="/schedules" 
          element={
            <PrivateRoute>
              <Dashboard user={user} onLogout={logout} />
            </PrivateRoute>
          } 
        />
        <Route 
          path="/appointments" 
          element={
            <PrivateRoute>
              <Dashboard user={user} onLogout={logout} />
            </PrivateRoute>
          } 
        />
        <Route 
          path="/notifications" 
          element={
            <PrivateRoute>
              <Dashboard user={user} onLogout={logout} />
            </PrivateRoute>
          } 
        />
        <Route 
          path="/schedule-settings" 
          element={
            <PrivateRoute>
              <ScheduleSettings />
            </PrivateRoute>
          } 
        />
        <Route 
          path="/settings" 
          element={
            <PrivateRoute>
              <Dashboard user={user} onLogout={logout} />
            </PrivateRoute>
          } 
        />
        <Route 
          path="/profile" 
          element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          } 
        />
        <Route 
          path="/create-entity" 
          element={
            <PrivateRoute>
              <CreateEntity />
            </PrivateRoute>
          } 
        />
        <Route 
          path="/success" 
          element={
            <PrivateRoute>
              <CheckoutSuccess />
            </PrivateRoute>
          } 
        />
        <Route 
          path="/appointment/:scheduleId" 
          element={
            <PrivateRoute>
              <FinalizeAppointment />
            </PrivateRoute>
          } 
        />
        <Route
          path="/medical-record"
          element={
            <PrivateRoute>
              <Dashboard user={user} onLogout={logout} />
            </PrivateRoute>
          }
        />
        <Route
          path="/medical-record/owner/:ownerId"
          element={
            <PrivateRoute>
              <Dashboard user={user} onLogout={logout} />
            </PrivateRoute>
          }
        />
        <Route
          path="/medical-record/pet/:petId"
          element={
            <PrivateRoute>
              <Dashboard user={user} onLogout={logout} />
            </PrivateRoute>
          }
        />
        <Route
          path="/medical-record/pet/:petId/consultation/new"
          element={
            <PrivateRoute>
              <Dashboard user={user} onLogout={logout} />
            </PrivateRoute>
          }
        />
        <Route
          path="/medical-record/pet/:petId/consultation/:consultationId"
          element={
            <PrivateRoute>
              <Dashboard user={user} onLogout={logout} />
            </PrivateRoute>
          }
        />
        <Route
          path="/medical-record/pet/:petId/consultation/:consultationId/edit"
          element={
            <PrivateRoute>
              <Dashboard user={user} onLogout={logout} />
            </PrivateRoute>
          }
        />
        <Route path="/cancel" element={<Navigate to="/dashboard" replace />} />
        {/* Public page — accessible without authentication */}
        <Route path="/vets" element={<Discover />} />
        <Route path="/vets/:entityId" element={<Discover />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#fff',
              color: '#333',
              fontSize: '14px',
              fontWeight: '500',
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              padding: '16px',
            },
            success: {
              iconTheme: {
                primary: '#10B981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#EF4444',
                secondary: '#fff',
              },
            },
          }}
        />
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
